import React, { useContext, useState, useMemo } from 'react'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  SetLinkRequest,
  LinkDoc,
} from '@trustero/trustero-api-web/lib/linker/linker_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { LinkerPromiseClient } from '@trustero/trustero-api-web/lib/linker/linker_grpc_web_pb'
import {
  useReceptor,
  useHasReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { PickerConfiguration } from '../modals/Google Drive Wizard/GoogleDriveWizard.constants'
import { Source } from '../Linkers.constants'
import { ExtendedDocumentObject, LINKER_MODEL_IDS } from '../Linkers.constants'

interface OnSubmitProps {
  caption: string
  isViaRequest?: boolean
  relevantDate?: Timestamp
}

type GoogleDriveContextType = {
  pickerConfig: PickerConfiguration | null
  setPickerConfig: React.Dispatch<
    React.SetStateAction<PickerConfiguration | null>
  >
  filesSelected?: ExtendedDocumentObject[]
  setFilesSelected?: React.Dispatch<
    React.SetStateAction<ExtendedDocumentObject[]>
  >
  onSubmit: (props: OnSubmitProps) => Promise<string[]>
  setSource: (source: Source) => void
  subjectIds: Array<string>
  setSubjectIds: (subjectId: Array<string>) => void
  hasGDriveReceptor: boolean
}

const GoogleDriveContext = React.createContext<
  GoogleDriveContextType | undefined
>(undefined)

export const useGoogleDriveContext = (): GoogleDriveContextType => {
  const context = useContext(GoogleDriveContext)
  if (!context) {
    throw new Error(
      'useGoogleDriveContext must be used within a GoogleDriveContextProvider',
    )
  }
  return context
}

export const GoogleDriveContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const { data: HasReceptor } = useHasReceptor(
    LINKER_MODEL_IDS.GOOGLE_DRIVE,
    true,
  )
  const hasGDriveReceptor = useMemo(
    () => HasReceptor?.getHasReceptor() || false,
    [HasReceptor],
  )
  const { data: receptor } = useReceptor({
    modelId: LINKER_MODEL_IDS.GOOGLE_DRIVE,
    shouldFetch: hasGDriveReceptor,
  })
  const [pickerConfig, setPickerConfig] = useState<PickerConfiguration | null>(
    null,
  )
  const linkerClient = useAuthorizedGrpcClient(LinkerPromiseClient)
  const [filesSelected, setFilesSelected] = useState<ExtendedDocumentObject[]>(
    [],
  )
  const [source, setSource] = useState<Source>('Evidence')
  const [subjectIds, setSubjectIds] = useState<Array<string>>([])
  const onSubmit = async ({
    caption,
    relevantDate,
    isViaRequest = false,
  }: OnSubmitProps): Promise<string[]> => {
    const request = new SetLinkRequest().setReceptorId(receptor?.getId() || '')
    const documents = filesSelected.map((doc) => {
      const linkDoc = new LinkDoc()
        .setImportKey(`${doc.id}:${source}`)
        .setImportLink(doc.url)
        .setSubjectIdsList(subjectIds)
        .setDocumentType(
          source === 'Evidence'
            ? DOCUMENT_TYPE.EVIDENCE
            : DOCUMENT_TYPE.POLICYDOC,
        )
        .setRelevantDate(relevantDate || undefined)
        .setIsViaRequest(isViaRequest)
        .setCaption(caption && caption.length > 0 ? caption : doc.name)
        .setFileName(doc.name)
      return linkDoc
    })
    setFilesSelected([])
    request.setDocumentsList(documents)
    const response = await linkerClient.setLink(request)
    return response.getEvidenceIdsList()
  }

  return (
    <GoogleDriveContext.Provider
      value={{
        hasGDriveReceptor,
        pickerConfig,
        setPickerConfig,
        filesSelected,
        setFilesSelected,
        onSubmit,
        setSource,
        subjectIds,
        setSubjectIds,
      }}
    >
      {children}
    </GoogleDriveContext.Provider>
  )
}
